// Where Unit Review's "Fix" control should send the user for a flagged product.
//
// Reasons that live in the ledger rows, correctable by relabelling one row.
const ROW_DRIVEN_REASONS = ['missing_unit', 'cross_family', 'pack_unit'];

// Reasons that describe the PRODUCT rather than any one row. These win: while
// the product's own stock unit is wrong, the relabel modal can only offer units
// from that wrong unit's family, so every correction it could suggest would
// preserve the number and change what it means. Fixing the product clears the
// rows that were recorded correctly all along.
const CONFIG_REASONS = ['invalid_stock_unit', 'stock_unit_mismatch'];

/**
 * How the "Fix" control behaves for a diagnostic row:
 * - 'link'  → deep-link to the product edit form.
 * - 'modal' → open the ledger-row relabel modal.
 *
 * An unrecognised reason falls through to 'link', because the product form is
 * the surface that can address a cause this function does not know about.
 */
export const fixModeFor = (r: { reasons: string[] }): 'link' | 'modal' => {
  if (r.reasons.some((x) => CONFIG_REASONS.includes(x))) return 'link';
  if (r.reasons.some((x) => ROW_DRIVEN_REASONS.includes(x))) return 'modal';
  return 'link';
};

/**
 * Route to the existing product edit surface for a given consumable type.
 *
 * Verified against frontend/src/App.tsx (2026-07-05):
 * - chemical:   `/protection/products/:id` is a real per-product route
 *   (SprayProductDetailPage renders SprayProductForm, which has the editable
 *   `stockUnit` <select>). Deep-linkable.
 * - fertilizer: there is NO `/nutrition/products/:id` route — the edit form
 *   is in-page state on NutritionProductsPage. That page accepts an `?edit=<id>`
 *   query param that auto-opens the matching product's form once the list
 *   loads, so we deep-link straight to the editable form.
 * - anything else (e.g. diesel/ppe): no dedicated module page exists yet;
 *   fall back to the generic consumables catalog detail page.
 */
export const editPathFor = (consumableType: string, productId: string): string => {
  if (consumableType === 'chemical') return `/protection/products/${productId}`;
  if (consumableType === 'fertilizer') return `/nutrition/products?edit=${productId}`;
  return `/consumables/catalog/${productId}`;
};
